import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Tooltip,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Add,
    Refresh,
    Search,
    AdminPanelSettings,
    Business,
    People,
    Edit,
    Delete,
    Visibility,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CustomAlert from '../UserManagement/CustomAlert';
import CustomButton from '../../../../components/CustomButton';
import { backendClient } from '../../../../api/backend';

import CreateOrganizationDialog from './dialogs/CreateOrganizationDialog';
import InviteUserDialog from './dialogs/InviteUserDialog';
import InviteOrganizationDialog from './dialogs/InviteOrganizationDialog';
import EditUserDialog from './dialogs/EditUserDialog';
import DeleteConfirmDialog from './dialogs/DeleteConfirmDialog';

const AVAILABLE_ROLES = ['user', 'admin', 'billing_admin', 'super_admin'];

const getRoleDisplayName = (role, t) => {
    switch (role) {
        case 'admin': return t('role_admin', 'Admin');
        case 'billing_admin': return t('role_billing_admin', 'Billing Admin');
        case 'super_admin': return t('role_super_admin', 'Super Admin');
        case 'user': return t('role_user', 'User');
        default: return role;
    }
};

const getStatusColor = (status, colors) => {
    switch (status) {
        case 'active': return { bg: alpha(colors.secondary, 0.12), text: colors.secondary };
        case 'trial': return { bg: alpha(colors.info, 0.12), text: colors.info };
        case 'cancelled': return { bg: alpha(colors.warning, 0.12), text: colors.warning };
        case 'inactive':
        case 'payment_required': return { bg: alpha(colors.error, 0.12), text: colors.error };
        default: return { bg: colors.backgroundAlt, text: colors.textSecondary };
    }
};

const PlatformManagement = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.auth?.user?.user);

    // Active panel
    const [activePanel, setActivePanel] = useState('organizations');

    // Data
    const [organizations, setOrganizations] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Search & filter
    const [searchQuery, setSearchQuery] = useState('');
    const [orgFilter, setOrgFilter] = useState('');

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Dialogs
    const [createOrgOpen, setCreateOrgOpen] = useState(false);
    const [inviteUserOpen, setInviteUserOpen] = useState(false);
    const [inviteOrgOpen, setInviteOrgOpen] = useState(false);
    const [editUserOpen, setEditUserOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'org'|'user', item }

    // Alert
    const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });

    const colors = {
        primary: theme.palette.primary.main,
        primaryLight: alpha(theme.palette.primary.main, 0.1),
        primaryDark: theme.palette.primary.dark,
        secondary: theme.palette.success.main,
        secondaryLight: alpha(theme.palette.success.main, 0.1),
        surface: theme.palette.background.paper,
        info: theme.palette.info.main,
        warning: theme.palette.warning.main,
        error: theme.palette.error.main,
        errorLight: alpha(theme.palette.error.main, 0.1),
        text: theme.palette.text.primary,
        textSecondary: theme.palette.text.secondary,
        border: theme.palette.divider,
        backgroundAlt: theme.palette.action.hover,
        isDark: theme.palette.mode === 'dark',
    };

    // ── Data fetching ────────────────────────────────────────────────────────

    const fetchOrganizations = useCallback(async () => {
        try {
            const data = await backendClient.getAdminOrganizations();
            setOrganizations(data);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            setAlertInfo({ open: true, message: t('error_fetching_organizations', 'Error fetching organizations'), severity: 'error' });
        }
    }, [t]);

    const fetchUsers = useCallback(async () => {
        try {
            const data = await backendClient.getAdminUsers(orgFilter || null);
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setAlertInfo({ open: true, message: t('error_fetching_users', 'Error fetching users'), severity: 'error' });
        }
    }, [orgFilter, t]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            if (activePanel === 'organizations') {
                await fetchOrganizations();
            } else {
                await fetchUsers();
            }
            setLoading(false);
        };
        load();
    }, [activePanel, refreshTrigger, fetchOrganizations, fetchUsers]);

    // Also fetch orgs for user panel filter dropdown
    useEffect(() => {
        if (activePanel === 'users' && organizations.length === 0) {
            fetchOrganizations();
        }
    }, [activePanel, organizations.length, fetchOrganizations]);

    const handleRefresh = () => {
        setRefreshTrigger((prev) => prev + 1);
        setSearchQuery('');
    };

    // ── Filtering ────────────────────────────────────────────────────────────

    const filteredOrgs = organizations.filter((org) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            org.name.toLowerCase().includes(q) ||
            (org.domain && org.domain.some((d) => d.toLowerCase().includes(q)))
        );
    });

    const filteredUsers = users.filter((user) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            user.email.toLowerCase().includes(q) ||
            (user.first_name && user.first_name.toLowerCase().includes(q)) ||
            (user.last_name && user.last_name.toLowerCase().includes(q)) ||
            (user.organization_name && user.organization_name.toLowerCase().includes(q))
        );
    });

    const displayData = activePanel === 'organizations' ? filteredOrgs : filteredUsers;

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handlePanelChange = (_, newPanel) => {
        if (newPanel) {
            setActivePanel(newPanel);
            setSearchQuery('');
            setPage(0);
        }
    };

    const showAlert = (message, severity = 'success') => {
        setAlertInfo({ open: true, message, severity });
    };

    // Organization handlers
    const handleCreateOrg = async (data) => {
        try {
            const newOrg = await backendClient.createAdminOrganization(data);
            setOrganizations((prev) => [newOrg, ...prev]);
            showAlert(t('organization_created', 'Organization created successfully'));
            setCreateOrgOpen(false);
        } catch (error) {
            showAlert(error.message || t('error_creating_organization', 'Error creating organization'), 'error');
        }
    };

    const handleViewOrg = (org) => {
        navigate(`/admin/organizations/${org.id}`);
    };

    const handleDeleteOrg = async (hard) => {
        if (!deleteTarget || deleteTarget.type !== 'org') return;
        try {
            await backendClient.deleteAdminOrganization(deleteTarget.item.id, hard);
            showAlert(
                hard
                    ? t('organization_hard_deleted', 'Organization permanently deleted')
                    : t('organization_deactivated', 'Organization deactivated'),
            );
            setDeleteOpen(false);
            setDeleteTarget(null);
            handleRefresh();
        } catch (error) {
            showAlert(error.message || t('error_deleting_organization', 'Error deleting organization'), 'error');
        }
    };

    // User handlers
    const handleInviteUser = async (data) => {
        try {
            await backendClient.inviteAdminUser(data);
            showAlert(t('invitation_sent_successfully', 'Invitation sent successfully'));
            setInviteUserOpen(false);
            handleRefresh();
        } catch (error) {
            showAlert(error.message || t('error_inviting_user', 'Error inviting user'), 'error');
        }
    };

    const handleInviteOrg = async (data) => {
        try {
            const newOrg = await backendClient.inviteOrg(data);
            setOrganizations((prev) => [newOrg, ...prev]);
            showAlert(t('org_invitation_sent', 'Organization created and invitation sent successfully'));
            setInviteOrgOpen(false);
        } catch (error) {
            if (error.existingUser) {
                throw error; // Let dialog handle it with info banner
            }
            showAlert(error.message || t('error_inviting_org', 'Error inviting organization'), 'error');
        }
    };

    const handleUpdateUser = async (userId, data) => {
        try {
            await backendClient.updateAdminUser(userId, data);
            showAlert(t('user_updated', 'User updated successfully'));
            setEditUserOpen(false);
            setSelectedUser(null);
            handleRefresh();
        } catch (error) {
            showAlert(error.message || t('error_updating_user', 'Error updating user'), 'error');
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget || deleteTarget.type !== 'user') return;
        try {
            await backendClient.deleteAdminUser(deleteTarget.item.id);
            showAlert(t('user_deleted_successfully', 'User deleted successfully'));
            setDeleteOpen(false);
            setDeleteTarget(null);
            handleRefresh();
        } catch (error) {
            showAlert(error.message || t('error_deleting_user', 'Error deleting user'), 'error');
        }
    };

    // ── Access check ─────────────────────────────────────────────────────────

    const isSuperAdmin = currentUser?.roles?.includes('super_admin');
    if (!isSuperAdmin) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>{t('access_denied', 'Access Denied')}</Typography>
                <Typography variant="body1">{t('super_admin_only', 'Only super admins can access platform management')}</Typography>
            </Box>
        );
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Box sx={{ py: 3, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3, mt: -0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        sx={{
                            mr: 2, p: 1.5, borderRadius: '12px',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <AdminPanelSettings />
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: colors.text, m: 0 }}
                    >
                        {t('platform_management', 'Platform Management')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={t('refresh', 'Refresh')}>
                        <IconButton
                            onClick={handleRefresh}
                            sx={{
                                backgroundColor: alpha(theme.palette.divider, 0.5),
                                '&:hover': { backgroundColor: theme.palette.divider },
                            }}
                        >
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Panel toggle + controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <ToggleButtonGroup
                    value={activePanel}
                    exclusive
                    onChange={handlePanelChange}
                    size="small"
                    sx={{
                        gap: 1.5,
                        '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2.5,
                            py: 0.8,
                            borderRadius: '8px !important',
                            border: `1px solid ${colors.border}`,
                            '&.Mui-selected': {
                                bgcolor: colors.primaryLight,
                                color: colors.primary,
                                borderColor: colors.primary,
                                '&:hover': { bgcolor: alpha(colors.primary, 0.15) },
                            },
                        },
                    }}
                >
                    <ToggleButton value="organizations">
                        <Business sx={{ mr: 1, fontSize: 18 }} />
                        {organizations.length} {t('organizations', 'Organizations')}
                    </ToggleButton>
                    <ToggleButton value="users">
                        <People sx={{ mr: 1, fontSize: 18 }} />
                        {organizations.reduce((sum, o) => sum + (o.user_count || 0), 0)} {t('users', 'Users')}
                    </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {activePanel === 'users' && (
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>{t('filter_by_org', 'Organization')}</InputLabel>
                            <Select
                                value={orgFilter}
                                label={t('filter_by_org', 'Organization')}
                                onChange={(e) => { setOrgFilter(e.target.value); setRefreshTrigger((p) => p + 1); }}
                                sx={{ borderRadius: '8px' }}
                            >
                                <MenuItem value="">{t('all_organizations', 'All Organizations')}</MenuItem>
                                {organizations.map((org) => (
                                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <TextField
                        placeholder={activePanel === 'organizations' ? t('search_organizations', 'Search organizations...') : t('search_users', 'Search users...')}
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                        sx={{
                            width: 280,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                transition: 'all 0.2s',
                                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.text.primary, 0.05)}` },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                            ),
                        }}
                    />

                    {activePanel === 'organizations' && (
                        <CustomButton
                            text={t('invite_organization', 'Invite Organization')}
                            outlined
                            startIcon={<Add />}
                            onClick={() => setInviteOrgOpen(true)}
                            sx={{
                                p: 1, borderRadius: '8px',
                            }}
                        />
                    )}
                    <CustomButton
                        text={activePanel === 'organizations' ? t('create_organization', 'Create Organization') : t('invite_user', 'Invite User')}
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => activePanel === 'organizations' ? setCreateOrgOpen(true) : setInviteUserOpen(true)}
                        sx={{
                            p: 1, borderRadius: '8px',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.1)}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.text.primary, 0.15)}`,
                                transform: 'translateY(-1px)',
                            },
                        }}
                    />
                </Box>
            </Box>

            {/* Table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: theme.palette.primary.main }} />
                </Box>
            ) : displayData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="textSecondary">{searchQuery ? t('no_search_results', 'No search results') : t('no_data', 'No data found')}</Typography>
                </Box>
            ) : activePanel === 'organizations' ? (
                /* Organizations table */
                <>
                    <TableContainer sx={{ borderRadius: '12px', border: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: colors.backgroundAlt }}>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('name', 'Name')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('domain', 'Domain')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }} align="center">{t('users', 'Users')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('plan', 'Plan')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('status.label', 'Status')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }} align="right">{t('actions', 'Actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredOrgs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((org) => {
                                    const subStatus = org.subscription?.status;
                                    const statusColors = getStatusColor(subStatus, colors);
                                    return (
                                        <TableRow key={org.id} hover sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }} onClick={() => handleViewOrg(org)}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text }}>{org.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                                    {org.domain?.join(', ') || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={org.user_count} size="small" sx={{ fontWeight: 600, bgcolor: colors.backgroundAlt }} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: colors.text, textTransform: 'capitalize' }}>
                                                    {org.subscription?.plan || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {subStatus ? (
                                                    <Chip
                                                        label={subStatus}
                                                        size="small"
                                                        sx={{ fontWeight: 600, bgcolor: statusColors.bg, color: statusColors.text, textTransform: 'capitalize' }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={t('view_details', 'View Details')}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewOrg(org); }} sx={{ color: colors.primary }}>
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('delete', 'Delete')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'org', item: org }); setDeleteOpen(true); }}
                                                        sx={{ color: colors.error }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredOrgs.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </>
            ) : (
                /* Users table */
                <>
                    <TableContainer sx={{ borderRadius: '12px', border: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: colors.backgroundAlt }}>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('email', 'Email')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('name', 'Name')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('organization', 'Organization')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('roles', 'Roles')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('status.label', 'Status')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('last_login', 'Last Login')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }} align="right">{t('actions', 'Actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                                    <TableRow key={user.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: colors.text }}>{user.email}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: colors.text }}>
                                                {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>{user.organization_name || '—'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {(user.roles || []).map((role) => (
                                                    <Chip
                                                        key={role}
                                                        label={getRoleDisplayName(role, t)}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 500, fontSize: '0.75rem',
                                                            bgcolor: role === 'super_admin' ? alpha(colors.warning, 0.12) :
                                                                role === 'admin' ? alpha(colors.secondary, 0.12) :
                                                                    role === 'billing_admin' ? alpha(colors.info, 0.12) :
                                                                        colors.backgroundAlt,
                                                            color: role === 'super_admin' ? colors.warning :
                                                                role === 'admin' ? colors.secondary :
                                                                    role === 'billing_admin' ? colors.info :
                                                                        colors.textSecondary,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.is_active && user.is_allowed ? t('active', 'Active') : t('inactive', 'Inactive')}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    bgcolor: user.is_active && user.is_allowed ? alpha(colors.secondary, 0.12) : alpha(colors.error, 0.12),
                                                    color: user.is_active && user.is_allowed ? colors.secondary : colors.error,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <Tooltip title={t('edit', 'Edit')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => { setSelectedUser(user); setEditUserOpen(true); }}
                                                        sx={{ color: colors.primary }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('delete', 'Delete')}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => { setDeleteTarget({ type: 'user', item: user }); setDeleteOpen(true); }}
                                                            sx={{ color: colors.error }}
                                                            disabled={user.id === currentUser?.id}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredUsers.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </>
            )}

            {/* Dialogs */}
            <CreateOrganizationDialog
                open={createOrgOpen}
                onClose={() => setCreateOrgOpen(false)}
                onSubmit={handleCreateOrg}
                colors={colors}
                t={t}
            />

            <InviteUserDialog
                open={inviteUserOpen}
                onClose={() => setInviteUserOpen(false)}
                onSubmit={handleInviteUser}
                organizations={organizations}
                availableRoles={AVAILABLE_ROLES}
                getRoleDisplayName={(role) => getRoleDisplayName(role, t)}
                colors={colors}
                t={t}
            />

            <InviteOrganizationDialog
                open={inviteOrgOpen}
                onClose={() => setInviteOrgOpen(false)}
                onSubmit={handleInviteOrg}
                colors={colors}
                t={t}
            />

            <EditUserDialog
                open={editUserOpen}
                onClose={() => { setEditUserOpen(false); setSelectedUser(null); }}
                user={selectedUser}
                onSubmit={handleUpdateUser}
                organizations={organizations}
                availableRoles={AVAILABLE_ROLES}
                getRoleDisplayName={(role) => getRoleDisplayName(role, t)}
                colors={colors}
                t={t}
            />

            <DeleteConfirmDialog
                open={deleteOpen}
                onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
                target={deleteTarget}
                onConfirm={deleteTarget?.type === 'org' ? handleDeleteOrg : handleDeleteUser}
                colors={colors}
                t={t}
            />

            <CustomAlert
                open={alertInfo.open}
                message={alertInfo.message}
                severity={alertInfo.severity}
                onClose={() => setAlertInfo({ ...alertInfo, open: false })}
            />
        </Box>
    );
};

export default PlatformManagement;
